import { create } from 'zustand';

export interface CartLine {
  /** Stable client-side id so we can mutate quantities even before order is placed. */
  lineId: string;
  menuItemId: string;
  nameAr: string;
  nameEn: string;
  unitPrice: number;
  currency: string;
  quantity: number;
  customizations: Record<string, string>;
  /** AI customization asset URL — display only; not sent to /api/orders. */
  previewImageUrl: string | null;
}

interface CartState {
  lines: CartLine[];
  isOpen: boolean;
  open: () => void;
  close: () => void;
  add: (line: Omit<CartLine, 'lineId' | 'quantity'> & { quantity?: number }) => void;
  setQuantity: (lineId: string, quantity: number) => void;
  remove: (lineId: string) => void;
  clear: () => void;
  subtotal: () => number;
}

export const useCartStore = create<CartState>((set, get) => ({
  lines: [],
  isOpen: false,
  open:  () => set({ isOpen: true }),
  close: () => set({ isOpen: false }),
  add: (input) =>
    set((s) => {
      const lineId = newLineId();
      const next: CartLine = {
        lineId,
        menuItemId: input.menuItemId,
        nameAr: input.nameAr,
        nameEn: input.nameEn,
        unitPrice: input.unitPrice,
        currency: input.currency,
        quantity: input.quantity ?? 1,
        customizations: input.customizations,
        previewImageUrl: input.previewImageUrl,
      };
      return { lines: [...s.lines, next], isOpen: true };
    }),
  setQuantity: (lineId, quantity) =>
    set((s) => ({
      lines: s.lines
        .map((l) => (l.lineId === lineId ? { ...l, quantity } : l))
        .filter((l) => l.quantity > 0),
    })),
  remove: (lineId) =>
    set((s) => ({ lines: s.lines.filter((l) => l.lineId !== lineId) })),
  clear: () => set({ lines: [] }),
  subtotal: () =>
    get().lines.reduce((sum, l) => sum + l.unitPrice * l.quantity, 0),
}));

function newLineId(): string {
  return Math.random().toString(36).slice(2) + Date.now().toString(36);
}
